import React, { useState } from "react";
import { toast } from "react-toastify";
import Modal from "../../../../components/Modal";
import { validateLedgerTemplateAction } from "../../../../actions/validateLedgerTemplateAction";
import { insertLedgerDetailsAction } from "../../../../actions/insertLedgerDetailsAction";
import "./index.scss";

const isRowDisabled = (row) => {
  if (!row) return true;

  // If the row is valid (isValid: true), it can be selected/unselected
  if (row.isValid === true) {
    return false;
  }

  // When row has error / invalid / false in API response:
  const msg = [
    row.validationMessage,
    row.message,
    row.status,
    row.statusMessage,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase()
    .trim();

  // "Ledger name already exists in entity" - only then can checkbox be select/unselecting
  const isLedgerExistsInEntity =
    msg.includes("ledger name already exists in entity") ||
    msg.includes("ledger already exists in entity") ||
    msg.includes("ledger name already exist in entity") ||
    msg.includes("ledger already exist in entity") ||
    (msg.includes("ledger") &&
      msg.includes("already exist") &&
      msg.includes("entity")) ||
    (msg.includes("ledger") && msg.includes("exist") && msg.includes("entity"));

  if (isLedgerExistsInEntity) {
    return false; // Can be selected/unselected
  }

  // Otherwise disable on the error / invalid / false API response
  return true;
};

const ValidateLedgerModal = ({
  isOpen,
  onClose,
  selectedEntities = [],
  onSuccess,
}) => {
  const [file, setFile] = useState(null);
  const [validating, setValidating] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedIndices, setSelectedIndices] = useState([]);

  const reset = () => {
    setFile(null);
    setValidationResult(null);
    setSelectedIndices([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setValidationResult(null);
    setSelectedIndices([]);
    setValidating(true);
    const result = await validateLedgerTemplateAction(f);
    setValidating(false);
    if (result.success) {
      const data = result.data || [];
      setValidationResult(data);
      // Pre-select all eligible (non-disabled) rows by default
      const initialSelected = data
        .map((row, idx) => ({ row, idx }))
        .filter(({ row }) => !isRowDisabled(row))
        .map(({ idx }) => idx);
      setSelectedIndices(initialSelected);
      toast.success(result.message || "Validation completed");
    } else {
      toast.error(result.message || "Validation failed");
      setFile(null);
      setSelectedIndices([]);
    }
  };

  const handleConfirmSave = async () => {
    if (!validationResult || selectedCount === 0) return;
    setSaving(true);

    // Build ledgerInsertRequests from ONLY selected rows that are not disabled
    const selectedRows = selectedIndices
      .map((idx) => validationResult[idx])
      .filter((row) => row && !isRowDisabled(row));

    if (selectedRows.length === 0) {
      toast.warning("Please select at least one row to save.");
      setSaving(false);
      return;
    }

    const ledgerInsertRequests = selectedRows.map((row) => ({
      entityId: row.entityId || "",
      companyName: row.companyName || "",
      ledgerName: row.ledgerName || "",
      gstNumbers: row.gstNumbers || "",
    }));

    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");
    const createdBy = userInfo.name || userInfo.username || "Admin";

    const result = await insertLedgerDetailsAction(
      ledgerInsertRequests,
      createdBy,
    );
    setSaving(false);

    if (result.success) {
      toast.success(result.message || "Ledger details saved successfully!");
      if (onSuccess) onSuccess();
      handleClose();
    } else {
      toast.error(result.message || "Failed to save ledger details");
    }
  };

  const validCount = validationResult?.filter((r) => r.isValid).length ?? 0;
  const invalidCount = validationResult
    ? validationResult.length - validCount
    : 0;
  const allValid =
    validationResult && validationResult.length > 0 && invalidCount === 0;

  const selectableIndices = (validationResult || [])
    .map((row, idx) => ({ row, idx }))
    .filter(({ row }) => !isRowDisabled(row))
    .map(({ idx }) => idx);

  const selectedCount = selectedIndices.filter(
    (idx) => validationResult?.[idx] && !isRowDisabled(validationResult[idx]),
  ).length;

  const canSave = selectedCount > 0 && !saving && !validating;

  const isAllSelected =
    selectableIndices.length > 0 &&
    selectableIndices.every((idx) => selectedIndices.includes(idx));

  const isIndeterminate =
    selectableIndices.some((idx) => selectedIndices.includes(idx)) &&
    !isAllSelected;

  const handleToggleRow = (index) => {
    const row = validationResult?.[index];
    if (!row || isRowDisabled(row)) return;

    setSelectedIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const handleToggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedIndices([]);
    } else {
      setSelectedIndices(selectableIndices);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Upload Bulk Ledger"
      size="large"
    >
      <div
        className="vlm-body"
        style={{
          padding: "15px 30px",
        }}
      >
        {/* ── Upload zone ── */}
        <div className="vlm-upload-section">
          <label className="vlm-upload-label" htmlFor="vlm-file-input">
            <input
              id="vlm-file-input"
              type="file"
              accept=".xlsx,.xls"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <div
              className={`vlm-upload-zone ${file ? (validationResult ? (allValid ? "vlm-upload-zone--valid" : "vlm-upload-zone--warn") : "vlm-upload-zone--selected") : ""}`}
            >
              {validating ? (
                <>
                  <div className="vlm-upload-zone__spinner" />
                  <span className="vlm-upload-zone__text">
                    Validating file…
                  </span>
                </>
              ) : file ? (
                <>
                  <span className="vlm-upload-zone__icon">📎</span>
                  <div className="vlm-upload-zone__info">
                    <span className="vlm-upload-zone__filename">
                      {file.name}
                    </span>
                    {validationResult && (
                      <span
                        className={`vlm-upload-zone__result ${allValid ? "vlm-upload-zone__result--ok" : "vlm-upload-zone__result--warn"}`}
                      >
                        {allValid
                          ? "✓ All entries valid"
                          : `⚠ ${invalidCount} issue${invalidCount !== 1 ? "s" : ""} found`}
                      </span>
                    )}
                  </div>
                  <span className="vlm-upload-zone__change">
                    Click to change
                  </span>
                </>
              ) : (
                <>
                  <svg
                    className="vlm-upload-zone__icon-svg"
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="16 16 12 12 8 16" />
                    <line x1="12" y1="12" x2="12" y2="21" />
                    <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3" />
                  </svg>
                  <span className="vlm-upload-zone__text">
                    Click to upload Excel file
                  </span>
                  <span className="vlm-upload-zone__hint">.xlsx or .xls</span>
                </>
              )}
            </div>
          </label>
        </div>

        {/* ── Summary pills ── */}
        {validationResult && (
          <div className="vlm-summary">
            <div className="vlm-summary__pill vlm-summary__pill--total">
              <span className="vlm-summary__num">
                {validationResult.length}
              </span>
              <span className="vlm-summary__lbl">Total</span>
            </div>
            <div className="vlm-summary__pill vlm-summary__pill--valid">
              <span className="vlm-summary__num">{validCount}</span>
              <span className="vlm-summary__lbl">Valid</span>
            </div>
            <div className="vlm-summary__pill vlm-summary__pill--invalid">
              <span className="vlm-summary__num">{invalidCount}</span>
              <span className="vlm-summary__lbl">Issues</span>
            </div>
            <div className="vlm-summary__pill vlm-summary__pill--selected">
              <span className="vlm-summary__num">{selectedCount}</span>
              <span className="vlm-summary__lbl">Selected</span>
            </div>
          </div>
        )}

        {/* ── Result table ── */}
        {validationResult && validationResult.length > 0 && (
          <div className="vlm-table-section">
            <div className="vlm-table-wrap">
              <table className="vlm-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Entity ID</th>
                    <th>Company Name</th>
                    <th>Ledger Name</th>
                    <th>Status</th>
                    <th style={{ textAlign: "center", width: "80px" }}>
                      <div
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "6px",
                          cursor:
                            selectableIndices.length > 0
                              ? "pointer"
                              : "default",
                        }}
                        title={
                          isAllSelected ? "Deselect all" : "Select all eligible"
                        }
                        onClick={(e) => {
                          if (e.target.tagName !== "INPUT") {
                            handleToggleSelectAll();
                          }
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = isIndeterminate;
                            }
                          }}
                          disabled={selectableIndices.length === 0}
                          onChange={handleToggleSelectAll}
                          style={{
                            cursor:
                              selectableIndices.length > 0
                                ? "pointer"
                                : "not-allowed",
                            width: "15px",
                            height: "15px",
                            accentColor: "#fe7c0b",
                          }}
                        />
                        <span>Select</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {validationResult.map((row, i) => {
                    const isOk = row.isValid === true;
                    const isDisabled = isRowDisabled(row);
                    const isSelected =
                      selectedIndices.includes(i) && !isDisabled;

                    return (
                      <tr
                        key={i}
                        className={isOk ? "vlm-row--ok" : "vlm-row--error"}
                      >
                        <td className="vlm-td--num">{i + 1}</td>
                        <td>
                          <span className="vlm-entity-id">
                            {row.entityId || "—"}
                          </span>
                        </td>
                        <td>{row.companyName || "—"}</td>
                        <td>
                          <span className="vlm-ledger-name">
                            {row.ledgerName || "—"}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`vlm-badge ${isOk ? "vlm-badge--success" : "vlm-badge--error"}`}
                          >
                            {isOk ? "✓ Valid" : `✗ ${row.validationMessage}`}
                          </span>
                        </td>
                        <td style={{ textAlign: "center", width: "80px" }}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            disabled={isDisabled}
                            onChange={() => handleToggleRow(i)}
                            title={
                              isDisabled
                                ? `Disabled: ${row.validationMessage || "Error / Invalid entry"}`
                                : isSelected
                                  ? "Unselect row"
                                  : "Select row"
                            }
                            style={{
                              cursor: isDisabled ? "not-allowed" : "pointer",
                              width: "16px",
                              height: "16px",
                              accentColor: "#fe7c0b",
                            }}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── Footer actions ── */}
        <div className="vlm-footer">
          {/* {validationResult && validationResult.length > 0 && (
            <div className="note-message">
              <svg
                className="note-message__icon"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>
                <strong>Note:</strong> Only selected ledgers will be processed
                during upload. Duplicate or already existing entries are
                excluded.
              </span>
            </div>
          )} */}

          <button
            className="vlm-btn vlm-btn--cancel"
            onClick={handleClose}
            disabled={saving}
          >
            Close
          </button>
          <button
            className="vlm-btn vlm-btn--submit"
            disabled={!canSave}
            onClick={handleConfirmSave}
          >
            {saving ? (
              <>
                <span className="vlm-btn__spinner" />
                Saving…
              </>
            ) : selectedCount > 0 ? (
              `✓ Confirm & Save (${selectedCount})`
            ) : (
              "✓ Confirm & Save"
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ValidateLedgerModal;
